import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { ProviderAccount } from '@prisma/client'
import { Avatar, Button, Flex, Form, Select, Space, Typography } from 'antd'
import { useState } from 'react'

type Props = {
  value: string | null
  onChange: (id: string) => void
  onEdit: (providerAccountId: string) => Promise<void>
  onDelete: (providerAccountId: string) => void

  providerAccounts: ProviderAccount[]
  addNewValue: string
}

export const ConnectionSelector: React.FC<Props> = ({
  value,
  onChange,
  onEdit,
  onDelete,
  providerAccounts,
  addNewValue,
}) => {
  const [deleteConfirmMap, setDeleteConfirmMap] = useState<
    Record<string, boolean>
  >({})
  const [isLoadingDelete, setLoadingDelete] = useState(false)

  const handleClickDelete = async (providerAccountId: string) => {
    if (deleteConfirmMap[providerAccountId]) {
      if (!isLoadingDelete) {
        setLoadingDelete(true)

        await onDelete(providerAccountId)

        setLoadingDelete(false)
        setDeleteConfirmMap(prev => ({ ...prev, [providerAccountId]: false }))
      }
    } else {
      setDeleteConfirmMap(prev => ({ ...prev, [providerAccountId]: true }))
    }
  }

  const connectionOptions = [
    ...providerAccounts.map(providerAccount => ({
      value: providerAccount.id,
      label: (
        <Flex justify="space-between" align="center">
          <Flex gap={8} align="center" style={{ flex: 1, minWidth: 0 }}>
            {providerAccount.pictureUrl ? (
              <Avatar
                shape="square"
                size="small"
                src={providerAccount.pictureUrl}
              />
            ) : (
              <UserOutlined />
            )}
            {providerAccount.status === 'CORRUPTED' ? (
              <Typography.Text
                type="warning"
                ellipsis
                title={`${providerAccount.name} - Re-connect your account to continue using this feature`}
                style={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Space>
                  <WarningOutlined />
                  {providerAccount.name} - Re-connect your account to continue
                  using this feature
                </Space>
              </Typography.Text>
            ) : (
              <Typography.Text
                ellipsis
                style={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {providerAccount.name}
              </Typography.Text>
            )}
          </Flex>
          <Flex style={{ flexShrink: 0 }}>
            <Button
              onClick={e => {
                e.stopPropagation()
                onEdit(providerAccount.id)
              }}
              size="small"
              type="text"
              icon={<EditOutlined />}
            />
            <Button
              onClick={e => {
                e.stopPropagation()
                handleClickDelete(providerAccount.id)
              }}
              danger={deleteConfirmMap[providerAccount.id] ? true : false}
              size="small"
              type="text"
              icon={<DeleteOutlined />}
            >
              {deleteConfirmMap[providerAccount.id] ? 'Really delete' : ''}
            </Button>
          </Flex>
        </Flex>
      ),
    })),
    {
      value: addNewValue,
      label: (
        <Flex gap={4} align="center">
          <PlusOutlined />
          Add new connection
        </Flex>
      ),
    },
  ]

  return (
    <Form layout="vertical">
      <Form.Item label="WordPress Connection">
        <Select
          value={value ?? undefined}
          onChange={onChange}
          placeholder="Select an existing connection"
          options={connectionOptions}
        />
      </Form.Item>
    </Form>
  )
}
